import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  n202Api, FN110, FN1121, FN1122, FN1123, FN114, FN116, FN117,
  N202CreateRequest,
} from '../../../services/n202Api';
import { useAuth } from '../../../contexts/AuthContext';
import { Mode } from './useN202Mode';

export interface N202DetailTables {
  vendor1Reasons: Partial<FN1121>[];
  vendor2Reasons: Partial<FN1122>[];
  vendor3Reasons: Partial<FN1123>[];
  receiptDetails: Partial<FN114>[];
  messages: Partial<FN116>[];
  attachments: Partial<FN117>[];
}

const emptyDetails = (): N202DetailTables => ({
  vendor1Reasons: [],
  vendor2Reasons: [],
  vendor3Reasons: [],
  receiptDetails: [],
  messages: [],
  attachments: [],
});

export function useN202Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FN110 | null>(null);
  const [formData, setFormData] = useState<Partial<FN110>>({});
  const [details, setDetails] = useState<N202DetailTables>(emptyDetails());
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<FN110 | null> => {
    setLoading(true);
    try {
      const data = await n202Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails({
        vendor1Reasons: data.vendor1_reasons || [],
        vendor2Reasons: data.vendor2_reasons || [],
        vendor3Reasons: data.vendor3_reasons || [],
        receiptDetails: data.receipt_details || [],
        messages: data.messages || [],
        attachments: data.attachments || [],
      });
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FN110, value: FN110[keyof FN110]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FN110): FN110[keyof FN110] => {
    return (formData as Record<string, FN110[keyof FN110]>)[key] ?? '';
  }, [formData]);

  /** Update a specific detail table */
  const setDetailTable = useCallback(<K extends keyof N202DetailTables>(
    tableKey: K,
    updater: (prev: N202DetailTables[K]) => N202DetailTables[K],
  ) => {
    setDetails(prev => ({ ...prev, [tableKey]: updater(prev[tableKey]) }));
  }, []);

  /** Build request payload */
  const buildRequest = useCallback((): N202CreateRequest => ({
    master: formData as FN110,
    vendor1_reasons: details.vendor1Reasons as FN1121[],
    vendor2_reasons: details.vendor2Reasons as FN1122[],
    vendor3_reasons: details.vendor3Reasons as FN1123[],
    receipt_details: details.receiptDetails as FN114[],
    messages: details.messages as FN116[],
    attachments: details.attachments as FN117[],
  }), [formData, details]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FN110) => void,
  ) => {
    setLoading(true);
    try {
      const req = buildRequest();
      if (mode === 'add') {
        const result = await n202Api.create(req);
        message.success(`新增成功: ${result.fn110_01}`);
        onSuccess(result);
      } else {
        const result = await n202Api.update(formData.fn110_01!, req);
        message.success('修改成功');
        applyResult(result);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        message.error(errors.map((er: { message: string }) => er.message).join('; '));
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, buildRequest]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await n202Api.delete(record.fn110_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Apply loaded/saved result */
  const applyResult = useCallback((result: FN110) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails({
      vendor1Reasons: result.vendor1_reasons || [],
      vendor2Reasons: result.vendor2_reasons || [],
      vendor3Reasons: result.vendor3_reasons || [],
      receiptDetails: result.receipt_details || [],
      messages: result.messages || [],
      attachments: result.attachments || [],
    });
  }, []);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    if (record) applyResult(record);
  }, [record, applyResult]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({
      fn110_23: 2,   // 預設記錄型態=採購
      fn110_132: user?.userId || '',
    });
    setDetails(emptyDetails());
  }, [user]);

  return {
    record, formData, details, loading,
    loadRecord, setField, getField,
    setDetailTable,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
  };
}
